// src/api/user.js
// Wrappers for the signed-in user's own data: expenses, receipts, claims,
// splits, subscription, categories, practitioner linking, connect codes.
// Returns unwrapped data or throws { message, code, status }.

import client from "./client";
import { tokenStore } from "./client";
import { API_URL } from "../config";

const unwrap = (res) => res.data.data;

const apiError = (err) => {
  const body = err.response?.data;
  const e = new Error(body?.error || "Something went wrong. Try again.");
  e.code = body?.code || "NETWORK";
  e.status = err.response?.status || 0;
  return e;
};

const run = async (promise) => {
  try {
    return unwrap(await promise);
  } catch (err) {
    throw apiError(err);
  }
};

// ---- expenses -----------------------------------------------------------
export const listExpenses = (params = {}) => run(client.get("/expenses", { params }));
export const expenseSummary = (params = {}) => run(client.get("/expenses/summary", { params }));
export const deductionsTotal = (params = {}) => run(client.get("/expenses/deductions-total", { params }));
export const toggleExpenseClaimed = (id) => run(client.patch(`/expenses/${id}/toggle`));
export const updateExpense = (id, body) => run(client.patch(`/expenses/${id}`, body));
export const deleteExpense = (id) => run(client.delete(`/expenses/${id}`));

// ---- receipts -----------------------------------------------------------
export const listReceipts = () => run(client.get("/receipts"));
export const getReceipt = (id) => run(client.get(`/receipts/${id}`));
export const getScanUsage = () => run(client.get("/receipts/usage"));
// authenticated image URL (served by the API, owner only)
export const receiptImageUrl = (id) => `${client.defaults.baseURL}/receipts/${id}/image`;

// ---- categories ---------------------------------------------------------
export const listCategories = () => run(client.get("/categories"));

// ---- claims -------------------------------------------------------------
export const listClaims = () => run(client.get("/claims"));
export const getClaim = (id) => run(client.get(`/claims/${id}`));
export const linkedCompanies = () => run(client.get("/claims/linked-companies"));
export const createClaim = (body) => run(client.post("/claims", body));
export const updateClaim = (id, body) => run(client.patch(`/claims/${id}`, body));
export const addClaimItems = (id, items) => run(client.post(`/claims/${id}/items`, { items }));
export const removeClaimItem = (id, itemId) => run(client.delete(`/claims/${id}/items/${itemId}`));
export const uploadClaimItemImage = (id, itemId, file) => {
  const form = new FormData();
  form.append("image", file);
  return run(client.post(`/claims/${id}/items/${itemId}/image`, form, { headers: { "Content-Type": "multipart/form-data" } }));
};
export const submitClaim = (id) => run(client.post(`/claims/${id}/submit`));
export const reopenClaim = (id) => run(client.post(`/claims/${id}/reopen`));
export const markClaimPaid = (id) => run(client.post(`/claims/${id}/paid`));
export const cancelClaim = (id) => run(client.post(`/claims/${id}/cancel`));
export const deleteClaim = (id) => run(client.delete(`/claims/${id}`));
export const getClaimMessages = (id) => run(client.get(`/claims/${id}/messages`));
export const sendClaimMessage = (id, body) => run(client.post(`/claims/${id}/messages`, { body }));

// ---- splits -------------------------------------------------------------
export const splitsSent = () => run(client.get("/splits/sent"));
export const splitsPending = () => run(client.get("/splits/pending"));
export const acceptSplit = (id) => run(client.post(`/splits/${id}/accept`));
export const declineSplit = (id) => run(client.post(`/splits/${id}/decline`));
export const cancelSplit = (id) => run(client.post(`/splits/${id}/cancel`));

// ---- subscription -------------------------------------------------------
export const getSubscription = () => run(client.get("/subscriptions"));
export const getPlans = () => run(client.get("/subscriptions/plans"));
export const upgradeIntent = (tier, voucherCode) =>
  run(client.post("/subscriptions/upgrade-intent", voucherCode ? { tier, voucher_code: voucherCode } : { tier }));
export const getBankingDetails = () => run(client.get("/subscriptions/banking"));
export const voucherPreview = (code) => run(client.post("/subscriptions/voucher-preview", { code }));

// ---- scan packs (prepaid top-up) ----------------------------------------
export const getPacks = () => run(client.get("/subscriptions/packs"));
export const packIntent = (packKey, companyId) =>
  run(client.post("/subscriptions/pack-intent", companyId ? { pack_key: packKey, company_id: companyId } : { pack_key: packKey }));

// Tax pack: streams a zip (xlsx + pdf + receipt images). Raw fetch so the
// binary isn't run through the JSON-unwrapping interceptor. Throws { message }.
export const downloadTaxPack = async (taxYear) => {
  const res = await fetch(`${API_URL}/expenses/tax-pack?tax_year=${taxYear}`, {
    headers: { Authorization: `Bearer ${tokenStore.access}` },
  });
  if (!res.ok) {
    let message = "Could not generate the tax pack.";
    try { const j = await res.json(); message = j.error || message; } catch (e) { /* non-JSON */ }
    throw new Error(message);
  }
  return res.blob();
};

// ---- practitioner linking ---------------------------------------------------
export const listPractitioners = () => run(client.get("/practitioners"));
export const unlinkPractitioner = (linkId) =>
  run(client.delete(`/practitioners/${linkId}`));

// ---- tax pack release (to practitioner) ------------------------------------
export const listReleases = () => run(client.get("/practitioners/releases"));
export const releaseTaxPack = (taxYear) =>
  run(client.post("/practitioners/release", { tax_year: taxYear }));
export const revokeRelease = (taxYear) =>
  run(client.delete(`/practitioners/releases/${taxYear}`));

// ---- connect code redemption (shared: works for company + practitioner) -----
export const redeemCode = (code) =>
  run(client.post("/connect-codes/redeem", { code }));